import React, {PureComponent} from "react";
import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import {getEvents} from "../../Helpers/EventHelper";
import {sharedEvents, itemNames, pageNames} from "../constants/productSpecification";
import AnalyticsContext from "./AnalyticsContext";

/**
 * This class wraps a component and exposes various Analytics related methods
 * to the context api which is then available to all nested components.
 */
export default class AnalyticsProvider extends PureComponent {
    static propTypes = {
        mixpanel: PropTypes.object.isRequired,
        productEvents: PropTypes.object,
        user: PropTypes.object,
    };

    state = {
        globalProperties: {},
    };

    componentDidMount() {
        if (this.props.user) {
            this.setUser(this.props.user);
        }
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.user, prevProps.user)) {
            if (this.props.user) {
                // if a prospect has an account role but no country, they have not completed sign
                // up. We should only setUser once they complete their profile to prevent
                // a race condition between aliasing and identifying the user
                if (this.props.user.accountRole === "applicant" && !this.props.user.country) {
                    // do nothing
                } else {
                    this.setUser(this.props.user);
                }
            } else {
                this.unsetUser();
            }
        }
    }

    /**
     * Sets a key-value pair that should be sent as a property with every analytics event.
     * @param {string} key - the property key to be sent with every event.
     * @param {string} value- the property value corresponding to the key to be sent with every event.
     */
    setAnalyticsValue = (key, value) => {
        this.setState(state => ({
            ...state,
            globalProperties: {
                ...state.globalProperties,
                [key]: value,
            },
        }));
    };

    /**
     * Calls the mixpanel API to set the user based on the email address
     * See https://help.mixpanel.com/hc/en-us/articles/115004497803-Identity-Management-Best-Practices
     * @param {object} user - user object containing email and account role
     */
    setUser = user => {
        try {
            /**
             * Until the alumni feature is fully fleshed out, we are using degree_level field
             * on the user object to differentiate the alumnis from others
             */
            let accountRole = user.accountRole;
            if (
                accountRole === "mentor" &&
                "degreeLevel" in user &&
                user.degreeLevel === "Alumni"
            ) {
                accountRole = "alumni";
            }
            this.props.mixpanel.people.set({
                id: user.id,
                $email: user.email,
                $first_name: user.firstName,
                accountRole,
            });

            this.props.mixpanel.identify(user.id);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Calls the Mixpanel API to alias a visitor to a user id, alias only called once in lifecycle of a user
     * See https://help.mixpanel.com/hc/en-us/articles/115004497803-Identity-Management-Best-Practices
     * @param {object} user - user object containing email and account role
     */
    aliasVisitor = user => {
        this.props.mixpanel.alias(user.id);
        /**
         * Until the alumni feature is fully fleshed out, we are using degree_level field
         * on the user object to differentiate the alumnis from others
         */
        let accountRole = user.accountRole;
        if (accountRole === "mentor" && "degreeLevel" in user && user.degreeLevel === "Alumni") {
            accountRole = "alumni";
        }
        this.props.mixpanel.people.set({
            id: user.id,
            $email: user.email,
            $first_name: user.firstName,
            accountRole,
        });
    };

    unsetAnalyticsValue = key => {
        this.setState(state => ({
            ...state,
            globalProperties: omit(state.globalProperties, key),
        }));
    };

    unsetUser = () => {
        this.props.mixpanel.reset();
    };

    /**
     * Calls the Mixpanel API to record an analytics event with properties
     * @param {string} name - name of the event
     * @param {object} properties - object of key-value pairs to send with the event
     */
    trackEvent = (name, properties) => {
        setTimeout(() => {
            try {
                this.props.mixpanel.track(name, {
                    ...this.state.globalProperties,
                    ...properties,
                });
            } catch (error) {
                // TODO: report error
                console.error(error);
            }
        }, 100); //100 milliseconds delay to avoid race condition with setState in AnalyticsValue
    };

    events = getEvents(this.trackEvent, sharedEvents, this.props.productEvents);

    analytics = {
        setAnalyticsValue: this.setAnalyticsValue,
        unsetAnalyticsValue: this.unsetAnalyticsValue,
        trackEvent: this.trackEvent,
        setUser: this.setUser,
        unsetUser: this.unsetUser,
        aliasVisitor: this.aliasVisitor,
        events: this.events,
        itemNames,
        pageNames,
    };

    render() {
        return (
            <AnalyticsContext.Provider value={this.analytics}>
                {this.props.children}
            </AnalyticsContext.Provider>
        );
    }
}
